/**
 * Security utilities for input validation and sanitization
 * Provides comprehensive form validation and input sanitization
 */

export class SecurityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SecurityError';
  }
}

/**
 * Form validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  sanitizedData?: Record<string, any>;
}

/**
 * Validation rule interface
 */
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  customValidator?: (value: any) => string | null;
}

/**
 * Validates and sanitizes date input to prevent injection attacks
 */
export const validateAndSanitizeDate = (date: Date | string | null | undefined): string => {
  if (!date) {
    throw new SecurityError('Date is required');
  }

  let dateObj: Date;
  
  if (typeof date === 'string') {
    // Validate date string format strictly
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new SecurityError('Invalid date format. Expected YYYY-MM-DD');
    }
    dateObj = new Date(date + 'T00:00:00.000Z');
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    throw new SecurityError('Invalid date type');
  }

  // Validate that it's a real date
  if (isNaN(dateObj.getTime())) {
    throw new SecurityError('Invalid date value');
  }

  // Validate reasonable date range (prevent far future/past dates)
  const minDate = new Date('1900-01-01');
  const maxDate = new Date('2100-12-31');
  
  if (dateObj < minDate || dateObj > maxDate) {
    throw new SecurityError('Date out of acceptable range');
  }

  // Return sanitized date string
  return dateObj.toISOString().split('T')[0];
};

/**
 * Validates time input to prevent injection
 */
export const validateAndSanitizeTime = (time: string | null | undefined): string => {
  if (!time) {
    throw new SecurityError('Time is required');
  }

  // Validate time format (HH:MM or HH:MM:SS)
  if (!/^([01]?\d|2[0-3]):([0-5]?\d)(:([0-5]?\d))?$/.test(time)) {
    throw new SecurityError('Invalid time format. Expected HH:MM or HH:MM:SS');
  }

  // Ensure we return consistent format (HH:MM:SS)
  const timeParts = time.split(':');
  const hours = timeParts[0].padStart(2, '0');
  const minutes = timeParts[1].padStart(2, '0');
  const seconds = timeParts[2] ? timeParts[2].padStart(2, '0') : '00';

  return `${hours}:${minutes}:${seconds}`;
};

/**
 * Sanitizes text input to prevent XSS
 */
export const sanitizeText = (text: string | null | undefined): string | null => {
  if (!text) return null;
  
  // Remove potentially dangerous characters
  return text
    .replace(/[<>"'&]/g, (match) => {
      const map: Record<string, string> = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;'
      };
      return map[match] || match;
    })
    .trim()
    .substring(0, 1000); // Limit length
};

/**
 * Validates numerical input
 */
export const validateNumber = (value: any, min?: number, max?: number): number => {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  
  if (isNaN(num) || !isFinite(num)) {
    throw new SecurityError('Invalid number');
  }

  if (min !== undefined && num < min) {
    throw new SecurityError(`Number must be at least ${min}`);
  }

  if (max !== undefined && num > max) {
    throw new SecurityError(`Number must be at most ${max}`);
  }

  return num;
};

/**
 * Validates enum values
 */
export const validateEnum = <T extends string>(value: any, allowedValues: T[]): T => {
  if (!allowedValues.includes(value)) {
    throw new SecurityError(`Invalid value. Must be one of: ${allowedValues.join(', ')}`);
  }
  return value;
};

/**
 * Validates email format with strict pattern
 */
export const validateEmail = (email: string): string => {
  if (!email || typeof email !== 'string') {
    throw new SecurityError('Email is required');
  }

  const sanitizedEmail = email.trim().toLowerCase();
  
  // RFC 5322 compliant email regex (simplified but secure)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(sanitizedEmail)) {
    throw new SecurityError('Invalid email format');
  }

  if (sanitizedEmail.length > 254) {
    throw new SecurityError('Email address too long');
  }

  return sanitizedEmail;
};

/**
 * Validates password strength
 */
export const validatePassword = (password: string): string => {
  if (!password || typeof password !== 'string') {
    throw new SecurityError('Password is required');
  }

  if (password.length < 8) {
    throw new SecurityError('Password must be at least 8 characters long');
  }

  if (password.length > 128) {
    throw new SecurityError('Password too long (max 128 characters)');
  }

  // Check for at least one uppercase, lowercase, number, and special character
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
    throw new SecurityError('Password must contain at least one uppercase letter, lowercase letter, number, and special character');
  }

  return password;
};

/**
 * Comprehensive form validator
 */
export const validateForm = (data: Record<string, any>, rules: Record<string, ValidationRule>): ValidationResult => {
  const errors: Record<string, string> = {};
  const sanitizedData: Record<string, any> = {};

  for (const [field, rule] of Object.entries(rules)) {
    const value = data[field];

    try {
      // Required field validation
      if (rule.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors[field] = `${field} is required`;
        continue;
      }

      // Skip further validation if field is empty and not required
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        sanitizedData[field] = value;
        continue;
      }

      let sanitizedValue = value;

      // String validations
      if (typeof value === 'string') {
        // Length validations
        if (rule.minLength && value.length < rule.minLength) {
          errors[field] = `${field} must be at least ${rule.minLength} characters`;
          continue;
        }

        if (rule.maxLength && value.length > rule.maxLength) {
          errors[field] = `${field} must be at most ${rule.maxLength} characters`;
          continue;
        }

        // Pattern validation
        if (rule.pattern && !rule.pattern.test(value)) {
          errors[field] = `${field} format is invalid`;
          continue;
        }

        // Sanitize string values
        sanitizedValue = sanitizeText(value);
      }

      // Custom validation
      if (rule.customValidator) {
        const customError = rule.customValidator(sanitizedValue);
        if (customError) {
          errors[field] = customError;
          continue;
        }
      }

      sanitizedData[field] = sanitizedValue;
    } catch (error) {
      if (error instanceof SecurityError) {
        errors[field] = error.message;
      } else {
        errors[field] = `Invalid ${field}`;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitizedData: Object.keys(errors).length === 0 ? sanitizedData : undefined
  };
};

/**
 * Role-based validation
 */
export const validateUserRole = (role: string): string => {
  const allowedRoles = ['admin', 'member', 'patient'];
  return validateEnum(role, allowedRoles);
};

/**
 * Phone number validation
 */
export const validatePhoneNumber = (phone: string): string => {
  if (!phone || typeof phone !== 'string') {
    throw new SecurityError('Phone number is required');
  }

  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  // Validate length (US format: 10 digits)
  if (digits.length !== 10) {
    throw new SecurityError('Phone number must be 10 digits');
  }

  // Format as (XXX) XXX-XXXX
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};